import { FC, useState, useCallback, useEffect, useRef } from "react";
import { 
  ReconciliationFormData, 
  ReconciliationStep, 
  ReconciliationInput,
  ReconciliationPreviewResponse
} from "@/types/reconciliation";
import {
  usePreviewReconciliationMutation,
  useProcessReconciliationMutation,
  useLazyGetDownloadStatusQuery,
  useLazyGetReportDownloadUrlQuery,
  useGetAllReportsQuery,
} from "@/services";
import { useSnackbar, useEffectiveClientScope } from "@/hooks";

import ReconciliationConfigModal from "../reconciliation-config";
import ReconciliationPreviewModal from "../reconciliation-preview";
import ReconciliationProgressModal from "../reconciliation-progress";

interface EnhancedReconciliationModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
  initialEntryLines?: string[];
  initialItems?: ValueDiscrepancyItem[];
}

const EnhancedReconciliationModal: FC<EnhancedReconciliationModalProps> = ({
  show,
  setShow,
  onClose,
  initialEntryLines,
  initialItems,
}) => {
  const { show: showSnackbar } = useSnackbar();
  const { clientId, tenantId, isTDAdmin, shouldRequireClientSelection } =
    useEffectiveClientScope();

  // Modal state management
  const [currentStep, setCurrentStep] = useState<ReconciliationStep>('configure');
  const [formData, setFormData] = useState<ReconciliationFormData | null>(null);
  const [previewData, setPreviewData] = useState<ReconciliationPreviewResponse | null>(null);
  
  // Processing state
  const [fileId, setFileId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [processingStatus, setProcessingStatus] = useState<'PENDING' | 'SUCCESS' | 'FAILED'>('PENDING');
  const [processingError, setProcessingError] = useState<string | null>(null);
  
  // Ref to store polling interval for cleanup. setTimeout (not setInterval)
  // so we can exponentially back off between checks instead of hammering
  // the API every 3s forever if the worker gets stuck.
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollStartedAtRef = useRef<number | null>(null);
  const pollAttemptRef = useRef<number>(0);

  // Polling policy
  //   1s → 2s → 5s → 10s → 15s → 30s → 60s (cap), hard-stopped at 20 min.
  // Backoff prevents rate-limit storms when the worker is slow/stuck.
  // The hard cap is a last-resort guard — the API itself now coerces
  // stale PENDING → FAILED after 15 min via the download service.
  const POLL_DELAYS_MS = [1000, 2000, 5000, 10000, 15000, 30000];
  const POLL_DELAY_CAP_MS = 60000;
  const POLL_MAX_DURATION_MS = 20 * 60 * 1000; // 20 minutes

  // API hooks
  const [previewReconciliation, { isLoading: isPreviewLoading, error: previewError }] = usePreviewReconciliationMutation();
  const [processReconciliation, { isLoading: isProcessing }] = useProcessReconciliationMutation();
  const [getDownloadStatus] = useLazyGetDownloadStatusQuery();
  const [getReportDownloadUrl] = useLazyGetReportDownloadUrlQuery();
  
  // Query to get reports for finding reportId by fileId - following main post-import page pattern
  const { data: reportsData, refetch } = useGetAllReportsQuery({
    // No reportType filter - get all reports to include VALUE_ADJUSTMENT and RATE_ADJUSTMENT
    pageSize: 50, // Get recent reports to find matching fileId
    clientId,
    ...(isTDAdmin && tenantId && { tenantId }),
  }, {
    refetchOnMountOrArgChange: true,
    skip: shouldRequireClientSelection,
  });
  

  // Transform form data to API input format
  const transformToApiInput = useCallback((data: ReconciliationFormData): ReconciliationInput => {
    if (!clientId) {
      throw new Error("No client selected");
    }

    // Check if any filters have actual values (not just empty arrays)
    const hasFilters =
      data.filters.reconFlag ||
      (data.filters.htsCodes && data.filters.htsCodes.length > 0) ||
      (data.filters.originCodes && data.filters.originCodes.length > 0) ||
      (data.filters.manufacturerIds && data.filters.manufacturerIds.length > 0) ||
      (data.filters.entryLines && data.filters.entryLines.length > 0);

    return {
      date_range: {
        start: data.dateRange.start,
        end: data.dateRange.end,
      },
      filters: hasFilters
        ? {
            ...(data.filters.reconFlag && { ReconFlag: data.filters.reconFlag }),
            ...(data.filters.htsCodes?.length && { hts_codes: data.filters.htsCodes }),
            ...(data.filters.originCodes?.length && { origin_codes: data.filters.originCodes }),
            ...(data.filters.manufacturerIds?.length && { manufacturer_ids: data.filters.manufacturerIds }),
            ...(data.filters.entryLines?.length && { entry_lines: data.filters.entryLines }),
          }
        : undefined,
      adjustment: {
        type: data.adjustmentType,
        data: data.adjustmentData,
      },
      clientId,
      ...(isTDAdmin && tenantId && { tenantId }),
    };
  }, [clientId, isTDAdmin, tenantId]);

  // Handle configuration submission
  const handleConfigSubmit = async (data: ReconciliationFormData) => {
    try {
      setFormData(data);
      const apiInput = transformToApiInput(data);
      
      const result = await previewReconciliation(apiInput).unwrap();
      setPreviewData(result);
      setCurrentStep('preview');
    } catch (error: any) {
      console.error('Preview failed:', error);
      showSnackbar('Error', 'Failed to generate preview. Please check your configuration.', 'error');
    }
  };


  // Handle preview confirmation
  const handlePreviewConfirm = async () => {
    if (!formData) return;

    try {
      const apiInput = transformToApiInput(formData);
      const result = await processReconciliation(apiInput).unwrap();
      
      setFileId(result.fileId);
      setCurrentStep('processing');
      
      // Validate result format
      if (!result.fileId) {
        throw new Error('Invalid response: missing fileId');
      }

      // Start polling for progress if needed
      if (result.progressBar && result.fileId) {
        startPolling(result.fileId);
      } else if (result.signedUrl) {
        // Direct download available
        setDownloadUrl(result.signedUrl);
        setProcessingStatus('SUCCESS');
        setCurrentStep('complete');
      } else {
        // Neither progressBar nor signedUrl - this shouldn't happen
        console.warn('Unexpected response format: no progressBar or signedUrl', result);
        // Default to polling
        startPolling(result.fileId);
      }
    } catch (error: any) {
      console.error('Processing failed:', error);
      setProcessingError(error?.data?.message || 'Processing failed. Please try again.');
      setProcessingStatus('FAILED');
    }
  };

  // Helper function to find report ID by fileId
  const findReportIdByFileId = useCallback((targetFileId: string, data?: typeof reportsData): string | null => {
    // Use provided data or fall back to component state
    const dataToSearch = data || reportsData;
    
    if (!dataToSearch?.data) {
      console.error(`No reports data available for fileId lookup: ${targetFileId}`);
      return null;
    }
    
    // Search for the report with matching fileId
    const matchingReport = dataToSearch.data.find(report => report.fileId === targetFileId);
    
    if (matchingReport) {
      return matchingReport._id;
    }
    
    console.error(`No report found with fileId: ${targetFileId}`);
    return null;
  }, [reportsData]);

  // Start polling for download status
  const startPolling = async (fileId: string) => {
    try {
      const result = await getDownloadStatus(fileId).unwrap();
      
      if (result.status === 'SUCCESS') {
        // Clear polling interval since processing is complete
        clearPollingInterval();
        
        // Find the corresponding report ID
        // Refetch to ensure we have the latest data since Lambda just completed
        const refetchResult = await refetch();
        
        // Use the fresh data from refetch result, not the stale component state
        const freshData = refetchResult.data;
        const reportId = findReportIdByFileId(fileId, freshData);
        
        if (reportId) {
          // Generate presigned URL using the new system
          try {
            const downloadResult = await getReportDownloadUrl({ reportId }).unwrap();
            setDownloadUrl(downloadResult);
            setProcessingStatus('SUCCESS');
            setProgress(100);
            setCurrentStep('complete');
          } catch (urlError: any) {
            console.error('Failed to generate download URL:', urlError);
            setProcessingError('Failed to generate download URL. Please try again.');
            setProcessingStatus('FAILED');
          }
        } else {
          setProcessingError('Could not find report for download. Please try again.');
          setProcessingStatus('FAILED');
        }
      } else if (result.status === 'FAILED') {
        // Clear polling interval since processing failed
        clearPollingInterval();
        setProcessingError(result.msg || 'Processing failed');
        setProcessingStatus('FAILED');
      } else {
        // Still processing, update progress
        setProgress(result.progress || 0);
        setProcessingStatus('PENDING');
      }
    } catch (error: any) {
      console.error('Status check failed:', error);
      setProcessingError('Failed to check processing status');
      setProcessingStatus('FAILED');
    }
  };

  // Cleanup function for polling timer. setTimeout based (not setInterval)
  // so each tick is self-scheduled with an exponentially-longer delay.
  const clearPollingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollStartedAtRef.current = null;
    pollAttemptRef.current = 0;
  }, []);

  // Self-scheduling poll with exponential backoff and a hard cap.
  //   - If the worker is crashing/hung, backoff means we don't spam the
  //     API every 3s for minutes on end.
  //   - The 20-min hard cap means a frontend tab that the user forgot
  //     about can't poll forever and trip rate limits.
  //   - The API-side staleness coerce (downloadService PENDING_TIMEOUT)
  //     is a second line of defense — it unsticks the record even if
  //     this cap doesn't fire (e.g. on a long-lived user session).
  useEffect(() => {
    clearPollingInterval();

    if (fileId && processingStatus === 'PENDING') {
      pollStartedAtRef.current = Date.now();
      pollAttemptRef.current = 0;

      const scheduleNext = () => {
        const elapsed = Date.now() - (pollStartedAtRef.current ?? Date.now());
        if (elapsed >= POLL_MAX_DURATION_MS) {
          setProcessingError(
            'Processing has taken too long. The worker may have crashed — please retry.'
          );
          setProcessingStatus('FAILED');
          return;
        }
        const attempt = pollAttemptRef.current++;
        const delay =
          attempt < POLL_DELAYS_MS.length
            ? POLL_DELAYS_MS[attempt]
            : POLL_DELAY_CAP_MS;

        pollingIntervalRef.current = setTimeout(async () => {
          await startPolling(fileId);
          // Re-read processingStatus after await — the poll may have
          // flipped state to SUCCESS/FAILED, in which case the effect
          // will re-run and clean up on its own. If it's still
          // PENDING, schedule the next tick.
          pollingIntervalRef.current = null;
          if (pollStartedAtRef.current != null) {
            scheduleNext();
          }
        }, delay);
      };

      scheduleNext();
    }

    return clearPollingInterval;
  }, [fileId, processingStatus, clearPollingInterval]);

  // Clean up when modal is closed
  useEffect(() => {
    if (!show) {
      // Clear polling and reset state
      clearPollingInterval();
      setProcessingStatus('PENDING');
      setFileId(null);
      setDownloadUrl(null);
      setProgress(0);
      setProcessingError(null);
    }
  }, [show, clearPollingInterval]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  // Handle download
  const handleDownload = async (url: string) => {
    try {
      // Open the presigned URL in a new tab for download
      window.open(url, '_blank');
      showSnackbar('Success', 'Download started successfully!', 'success');
    } catch (error) {
      console.error('Download failed:', error);
      showSnackbar('Error', 'Failed to download file. Please try again.', 'error');
    }
  };


  // Handle modal close
  const handleClose = () => {
    setShow(false);
    
    // Call onClose callback to trigger reports refetch
    if (onClose) {
      onClose();
    }
    
    // Reset state after a short delay to avoid visual glitches
    setTimeout(() => {
      setCurrentStep('configure');
      setFormData(null);
      setPreviewData(null);
      setFileId(null);
      setDownloadUrl(null);
      setProgress(0);
      setProcessingStatus('PENDING');
      setProcessingError(null);
    }, 300);
  };

  // Determine which modal to show
  const showConfigModal = show && currentStep === 'configure';
  const showPreviewModal = show && currentStep === 'preview' && formData !== null;
  const showProgressModal = show && (currentStep === 'processing' || currentStep === 'complete');

  return (
    <>
      <ReconciliationConfigModal
        show={showConfigModal}
        setShow={(show) => show ? setShow(true) : handleClose()}
        onSubmit={handleConfigSubmit}
        isLoading={isPreviewLoading}
        initialEntryLines={initialEntryLines}
        initialItems={initialItems}
      />
      
      <ReconciliationPreviewModal
        show={showPreviewModal}
        setShow={(show) => show ? setShow(true) : handleClose()}
        onConfirm={handlePreviewConfirm}
        previewData={previewData || undefined}
        formData={formData || undefined}
        isLoading={isPreviewLoading}
        isProcessing={isProcessing}
        error={previewError ? 'Failed to generate preview' : undefined}
      />
      
      <ReconciliationProgressModal
        show={showProgressModal}
        setShow={(show) => show ? setShow(true) : handleClose()}
        onDownload={handleDownload}
        onClose={handleClose}
        fileId={fileId || undefined}
        status={processingStatus}
        progress={progress}
        downloadUrl={downloadUrl || undefined}
        error={processingError || undefined}
        adjustmentType={formData?.adjustmentType}
      />
    </>
  );
};

export default EnhancedReconciliationModal;